import { ref } from 'vue';
// eslint-disable-next-line import/no-unresolved, import/extensions
import gqlRequest from '../dashboard/gql-request';
// eslint-disable-next-line import/no-unresolved, import/extensions
import {
  getStaticPageErrorDetails,
  StaticPageErrorDetails,
  StaticPageErrorType,
} from './staticPageErrors';
import {
  StaticPageData,
  Block,
  BlockTypes,
  BannerBlock,
  SearchBarBlock,
  StickersBlock,
  OrganizationsBlock,
  RichTextBlock,
  AnnouncementBlock,
  QuestionBlock,
  CollectionsBlock,
  ListBlock,
} from './types';

/**
 * Composable for managing static page data and operations
 */
export function useStaticPage() {
  const {
    fetchPublicStaticPage,
    submitFeaturedQuestionAnswer,
  } = gqlRequest();

  const loading = ref(false);
  const error = ref(false);
  const errorMessage = ref('');
  const errorTitle = ref('');
  const errorType = ref<StaticPageErrorType>('unknown');

  /**
   * Process a single block and enrich it with related data
   * Returns null if block cannot be processed (missing data, unsupported type, etc.)
   */
  const processBlock = (
    block: any,
    rawData: any,
    organizationsMap: Map<string, any>,
    stickersMap: Map<string, any>,
  ): Block | null => {
    try {
      const blockType = block.blockType as BlockTypes;

      // Map block types to their data arrays
      const blockTypeDataMap: { [key: string]: { data: any[]; key: string } } = {
        [BlockTypes.BANNER]: { data: rawData.bannerBlocks || [], key: 'bannerBlocks' },
        [BlockTypes.SEARCH_BAR]: {
          data: rawData.searchBarBlocks || [],
          key: 'searchBarBlocks',
        },
        [BlockTypes.STICKERS]: {
          data: rawData.stickersBlocks || [],
          key: 'stickersBlocks',
        },
        [BlockTypes.ORGANIZATIONS]: {
          data: rawData.organizationsBlocks || [],
          key: 'organizationsBlocks',
        },
        [BlockTypes.RICH_TEXT]: {
          data: rawData.richTextBlocks || [],
          key: 'richTextBlocks',
        },
        [BlockTypes.ANNOUNCEMENT]: {
          data: rawData.announcementBlocks || [],
          key: 'announcementBlocks',
        },
        [BlockTypes.QUESTION]: {
          data: rawData.questionBlocks || [],
          key: 'questionBlocks',
        },
        [BlockTypes.COLLECTIONS]: {
          data: rawData.collectionsBlocks || [],
          key: 'collectionsBlocks',
        },
        [BlockTypes.LIST]: { data: rawData.listBlocks || [], key: 'listBlocks' },
      };

      const blockTypeInfo = blockTypeDataMap[blockType];
      if (!blockTypeInfo) {
        console.warn(`Unsupported block type: ${block.blockType}`);
        return null;
      }

      const blockData = blockTypeInfo.data.find((b: any) => b.id === block.id);
      if (!blockData) {
        console.warn(`Block data not found for block ID: ${block.id}`);
        return null;
      }

      // Process based on block type
      switch (blockType) {
        case BlockTypes.BANNER: {
          const bannerBlock: BannerBlock = {
            ...blockData,
            id: block.id,
            sortOrder: block.sortOrder,
            blockType: BlockTypes.BANNER,
          };
          return bannerBlock;
        }

        case BlockTypes.SEARCH_BAR: {
          const searchBarBlock: SearchBarBlock = {
            ...blockData,
            id: block.id,
            sortOrder: block.sortOrder,
            blockType: BlockTypes.SEARCH_BAR,
          };
          return searchBarBlock;
        }

        case BlockTypes.STICKERS: {
          const stickerBlock: StickersBlock = {
            ...blockData,
            id: block.id,
            sortOrder: block.sortOrder,
            blockType: BlockTypes.STICKERS,
            stickers: blockData.stickersList
              .map((id: string) => stickersMap.get(id))
              .filter(Boolean),
          };
          return stickerBlock;
        }

        case BlockTypes.ORGANIZATIONS: {
          const orgBlock: OrganizationsBlock = {
            ...blockData,
            id: block.id,
            sortOrder: block.sortOrder,
            blockType: BlockTypes.ORGANIZATIONS,
            organizations: blockData.organizationsList
              .map((id: string) => organizationsMap.get(id))
              .filter(Boolean),
          };
          return orgBlock;
        }

        case BlockTypes.RICH_TEXT: {
          const richTextBlock: RichTextBlock = {
            ...blockData,
            id: block.id,
            sortOrder: block.sortOrder,
            blockType: BlockTypes.RICH_TEXT,
          };
          return richTextBlock;
        }

        case BlockTypes.ANNOUNCEMENT: {
          const announcementBlock: AnnouncementBlock = {
            ...blockData,
            id: block.id,
            sortOrder: block.sortOrder,
            blockType: BlockTypes.ANNOUNCEMENT,
          };
          return announcementBlock;
        }

        case BlockTypes.QUESTION: {
          // Resolve organization from organizationsMap if available
          let organization = null;
          if (blockData.organizationId) {
            organization = organizationsMap.get(blockData.organizationId) || null;
          }

          const questionBlock: QuestionBlock = {
            ...blockData,
            id: block.id,
            sortOrder: block.sortOrder,
            blockType: BlockTypes.QUESTION,
            organization,
          };
          return questionBlock;
        }

        case BlockTypes.COLLECTIONS: {
          const processedGroups = blockData.groups.map((group: any) => ({
            ...group,
            organizations: group.organizationsList
              .map((id: string) => organizationsMap.get(id))
              .filter(Boolean),
          }));

          const collectionBlock: CollectionsBlock = {
            ...blockData,
            id: block.id,
            sortOrder: block.sortOrder,
            blockType: BlockTypes.COLLECTIONS,
            groups: processedGroups,
          };
          return collectionBlock;
        }

        case BlockTypes.LIST: {
          const listBlock: ListBlock = {
            ...blockData,
            id: block.id,
            sortOrder: block.sortOrder,
            blockType: BlockTypes.LIST,
            organizations: blockData.organizationsList
              .map((id: string) => organizationsMap.get(id))
              .filter(Boolean),
          };
          return listBlock;
        }

        default:
          console.warn(`Unknown block type: ${blockType}`);
          return null;
      }
    } catch (err) {
      console.error(`Error processing block ${block.id}:`, err);
      return null;
    }
  };

  /**
   * Process static page data and populate blocks with actual data
   * Similar to Flutter's StaticPageProcessor
   */
  const processStaticPage = async (rawData: any): Promise<StaticPageData> => {
    // Create maps for easy lookup from the data included in the response
    const organizationsMap = new Map<string, any>();
    const stickersMap = new Map<string, any>();

    // Populate organizations map
    if (rawData.organizations) {
      rawData.organizations.forEach((org: any) => {
        if (org.id) {
          organizationsMap.set(org.id, org);
        }
      });
    }

    // Populate stickers map
    if (rawData.stickers) {
      rawData.stickers.forEach((sticker: any) => {
        if (sticker.id) {
          stickersMap.set(sticker.id, sticker);
        }
      });
    }

    // Process each block and create unified block list
    const processedBlocks: Block[] = [];

    if (rawData.blocks) {
      rawData.blocks.forEach((block: any) => {
        const processedBlock = processBlock(
          block,
          rawData,
          organizationsMap,
          stickersMap,
        );

        // Only add if processing was successful (skip missing blocks gracefully)
        if (processedBlock) {
          processedBlocks.push(processedBlock);
        }
      });
    }

    // Sort by sortOrder to ensure correct display order
    processedBlocks.sort((a, b) => a.sortOrder - b.sortOrder);

    return {
      id: rawData.id,
      slug: rawData.slug,
      title: rawData.title,
      greenGradientEnabled: rawData.greenGradientEnabled || false,
      blocks: processedBlocks,
      organizations: rawData.organizations,
      stickers: rawData.stickers,
    };
  };

  /**
   * Fetch and process a static page by slug
   */
  const setPageError = (err: unknown) => {
    const details: StaticPageErrorDetails = getStaticPageErrorDetails(err);
    error.value = true;
    errorTitle.value = details.title;
    errorMessage.value = details.message;
    errorType.value = details.type;
  };

  const getStaticPage = async (
    slug: string,
  ): Promise<StaticPageData | null> => {
    try {
      loading.value = true;
      error.value = false;
      errorMessage.value = '';
      errorTitle.value = '';
      errorType.value = 'unknown';

      const rawData = await fetchPublicStaticPage(slug);

      if (!rawData) {
        throw new Error('PAGE_NOT_FOUND');
      }

      const processedData = await processStaticPage(rawData);

      return processedData;
    } catch (err: any) {
      setPageError(err);
      console.error('Error fetching static page:', err);
      return null;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Submit a featured question answer
   */
  const submitAnswer = async (
    questionId: string,
    content: string,
  ): Promise<boolean> => {
    try {
      await submitFeaturedQuestionAnswer(questionId, content);
      return true;
    } catch (err: any) {
      console.error('Error submitting answer:', err);
      return false;
    }
  };

  return {
    loading,
    error,
    errorMessage,
    errorTitle,
    errorType,
    getStaticPage,
    submitAnswer,
    processStaticPage,
  };
}
